import React, { useState } from "react";
import styled from "@emotion/styled";
import { ContactDescription } from "../types";
import SpeechRecognition from "react-speech-recognition";
import Image from "./image";
import { getImageUrl, formatDate } from "../utils";
import {
    TextField,
    Button,
    Autocomplete,
    MenuItem,
    Checkbox,
} from "@mui/material";
import DatePicker from "./date-picker";
import Dropdown from "./dropdown";
import { PrimaryText } from "./common";
import dayjs, { Dayjs } from "dayjs";
import { ReactComponent as BellIcon } from "../assets/bell.svg";
import { ReactComponent as BellSlashIcon } from "../assets/bell-slash.svg";
import { ReactComponent as LockIcon } from "../assets/lock.svg";
import { connect } from "react-redux";
import { RootState } from "../redux/root-store";
import Confirm from "./confirm";
import SpeechToTextButton from "./contacts/speech-to-text-button";

export const Container = styled("div")(
    ({ theme, $isEditing, $isShared }: any) => `
    border: 1px solid #ccc;
    margin-bottom: 1em;
    padding: 1em 1em 2em 1em;
    position: relative;
    ${!$isEditing && `padding-right: 4rem;`}
    &:hover {
        ${
            !$isEditing &&
            `background-color: ${theme.palette.grey2.light};
            ${(!$isShared && `cursor: pointer;`) || ""}
            `
        }
    }
`
);

const KidImage = styled(Image)`
    max-width: min(300px, 100%);
    max-height: 10em;
`;

const EditRow = styled.div`
    display: flex;
    align-items: end;
    margin: 0.4em 0;
`;

export const CenterEditRow = styled(EditRow)`
    align-items: center;
`;

const FactCreationDate = styled("span")(
    ({ theme }: any) => `
      color: ${theme.palette.grey2.main};
      font-size: 0.7rem;`
);

const mapStateToProps = (state: RootState) => ({
    loggedUser: state.session.user,
});

interface Props extends ReturnType<typeof mapStateToProps> {
    fact: ContactDescription;
    newFact?: boolean;
    discardNewFact?: (desc_id: string) => void;
    contactId?: string;
    saveContactDescription: any;
    deleteFact: any;
    hideReminder?: boolean;
}

const DESCRIPTION_MAX_LENGTH = 140;

const FactCard = (props: Props) => {
    const { fact, saveContactDescription, deleteFact } = props;
    const [newFact, setNewFact] = useState<ContactDescription>({
        ...fact,

        inAdvanceFirstWord:
            fact.inAdvanceFirstWord || (fact.factKind === 3 && "daily") || "",
        inAdvanceSecondWord:
            fact.inAdvanceSecondWord || (fact.factKind === 3 && "2") || "",
        inAdvanceThirdWord:
            fact.inAdvanceThirdWord || (fact.factKind === 3 && "weeks") || "",
    });
    const [placesService, setPlacesService] = useState<any>(null);
    const [editFact, setEditFact] = useState<boolean>(props.newFact || false);
    const [personProfile, setPersonProfile] = useState<File | null>(null);
    const [locationOptions, setLocationOptions] = useState<
        { label: string; id: number; lat: number; lng: number }[]
    >([]);
    const [locationValue, setLocationValue] = useState<{
        label: string;
        id: number;
        lat: number;
        lng: number;
    } | null>(null);
    const [sendReminder, setSendReminder] = useState<boolean>(false);
    const [sharedFactWarning, setSharedFactWarning] = useState<boolean>(false);
    const [isSettingSpecificReminderDate, setIsSettingSpecificReminderDate] =
        useState<boolean>(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const preventBlurTimeout = React.useRef<number>(0);
    const PREVENT_BLUR_TIMEOUT = 500;

    const handleSave = async () => {
        if (props.fact.sharedFact) {
            return;
        }
        // remove the fact from the pending to save list
        if (props.newFact && props.discardNewFact) {
            props.discardNewFact(fact.desc_id);
        }
        setPersonProfile(null);
        setEditFact(false);
        SpeechRecognition.stopListening();

        // don't save the fact if it's empty
        if (newFact.factKind === 0 && newFact.description === "") return;
        if (newFact.factKind === 1 && newFact.location === "") return;
        if (newFact.factKind === 3 && newFact.birthdayDate === "") return;
        if (!props.loggedUser) return;
        const savedFact = await saveContactDescription(
            {
                ...newFact,
                contact_id: props.newFact
                    ? props.contactId || ""
                    : fact.contact_id,
            },
            props.loggedUser.user_id,
            personProfile || undefined
        );
    };

    const handleDiscard = () => {
        setPersonProfile(null);
        setEditFact(false);
        SpeechRecognition.stopListening();
        setLocationOptions([]);
        setLocationValue(null);
        if (props.newFact && props.discardNewFact) {
            props.discardNewFact(fact.desc_id);
        }
    };

    const handleStartEdit = () => {
        if (fact.sharedFact) {
            setSharedFactWarning(true);
            return;
        }
        setNewFact({
            ...fact,
            inAdvanceFirstWord:
                fact.inAdvanceFirstWord ||
                (fact.factKind === 3 && "daily") ||
                "",
            inAdvanceSecondWord:
                fact.inAdvanceSecondWord || (fact.factKind === 3 && "2") || "",
            inAdvanceThirdWord:
                fact.inAdvanceThirdWord ||
                (fact.factKind === 3 && "weeks") ||
                "",
        });
        setEditFact(true);
        setLocationValue(null);
        setSendReminder(fact.remindOnDate ? true : false);
        setLocationOptions([]);
    };

    const handleChangeFactReminder = (frequency: string) => {
        if (fact.sharedFact) return;
        setNewFact({
            ...newFact,
            remindOnDate: frequency,
        });
    };

    const handleDelete = async () => {
        await deleteFact(fact);
    };

    const getReminderText = (frequency: string) => {
        switch (frequency) {
            case "12#Months":
                return "Yearly";
            case "1#Weeks":
                return "Weekly";
            case "1#Months":
                return "Monthly";
            case "6#Months":
                return "Every 6 Months";
            default:
                if (frequency && !frequency.includes("#")) {
                    return dayjs
                        .unix(parseInt(frequency))
                        .format("MMM DD YYYY");
                }
                return "Randomly";
        }
    };

    const handleToggleNotifications = (evt: React.SyntheticEvent) => {
        evt.stopPropagation();
        evt.preventDefault();
        if (fact.sharedFact) return;
        if (!props.loggedUser) return;
        saveContactDescription(
            { ...fact, factNotif: !fact.factNotif },
            props.loggedUser.user_id
        );
    };

    const BLUR_PREVENTION_CLASSNAMES = [
        "MuiTypography-root",
        "MuiDialog-scrollPaper",
        "MuiButtonBase-root",
        "PrivatePickersYear-yearButton",
    ];

    const onTranscript = React.useCallback((text: string) => {
        setNewFact((prevFact) => ({
            ...prevFact,
            description: text,
        }));
    }, []);

    return (
        <Container
            $isEditing={editFact}
            $isShared={fact.sharedFact}
            tabIndex={0}
            style={{ paddingBottom: editFact ? "2em" : "1em" }}
            onClick={() => {
                editFact === false && handleStartEdit();
            }}
            onBlur={(evt: React.FocusEvent<HTMLDivElement>) => {
                // if (fact.factKind === 2) return;
                if (evt.relatedTarget?.className.includes(fact.desc_id)) return;
                if (
                    preventBlurTimeout.current &&
                    new Date().getTime() < preventBlurTimeout.current &&
                    evt.relatedTarget === null
                )
                    return;
                if (
                    evt.currentTarget.contains(evt.relatedTarget) ||
                    (evt.relatedTarget &&
                        BLUR_PREVENTION_CLASSNAMES.some(
                            (preventBlurClass: string) =>
                                evt.relatedTarget?.className.includes(
                                    preventBlurClass
                                )
                        ))
                ) {
                } else {
                    handleSave();
                }
            }}
            onFocus={() => {}}
        >
            {(fact.factKind === 0 || fact.imported_from_google_calendar) && (
                <div>
                    {editFact ? (
                        <div>
                            <EditRow style={{ position: "relative" }}>
                                <TextField
                                    multiline
                                    style={{ width: "100%" }}
                                    inputProps={{
                                        maxLength: DESCRIPTION_MAX_LENGTH,
                                    }}
                                    value={newFact.description}
                                    label="Note"
                                    autoFocus
                                    onChange={(
                                        evt: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        setNewFact({
                                            ...newFact,
                                            description: evt.target.value,
                                        });
                                    }}
                                />
                                <FactCreationDate
                                    style={{
                                        position: "absolute",
                                        bottom: "0rem",
                                        right: "1rem",
                                    }}
                                >
                                    (
                                    {DESCRIPTION_MAX_LENGTH -
                                        newFact.description.length}{" "}
                                    characters left)
                                </FactCreationDate>
                            </EditRow>
                            <div className="flex justify-between">
                                <div>
                                    <img
                                        style={{
                                            maxWidth: "50%",
                                            maxHeight: "10em",
                                            marginTop: "1em",
                                        }}
                                        src={
                                            personProfile
                                                ? newFact.person_profile
                                                : getImageUrl(
                                                      fact.person_profile
                                                  )
                                        }
                                        alt=""
                                    />
                                    <Button
                                        tabIndex={0}
                                        className={fact.desc_id}
                                        onClick={(
                                            evt: React.SyntheticEvent
                                        ) => {
                                            inputRef.current &&
                                                inputRef.current.click();
                                            evt.stopPropagation();
                                            evt.preventDefault();
                                            preventBlurTimeout.current =
                                                new Date().getTime() +
                                                PREVENT_BLUR_TIMEOUT;
                                        }}
                                    >
                                        Attach picture
                                    </Button>
                                </div>
                                <SpeechToTextButton
                                    onTranscript={onTranscript}
                                />
                            </div>
                            <input
                                onFocus={() => {}}
                                tabIndex={0}
                                className={fact.desc_id}
                                ref={inputRef}
                                id={`${fact.desc_id}-kid-picture-input`}
                                style={{ display: "none" }}
                                type="file"
                                accept="image/*"
                                onChange={(
                                    evt: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    if (!evt.target.files) return;
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        setNewFact((newFact) => ({
                                            ...newFact,
                                            person_profile:
                                                (reader.result &&
                                                    reader.result.toString()) ||
                                                "",
                                        }));
                                    };
                                    reader.readAsDataURL(evt.target.files[0]);
                                    setPersonProfile(evt.target.files[0]);
                                }}
                            />
                        </div>
                    ) : (
                        <div>
                            <span>{fact.description}</span>
                            {fact.person_profile && (
                                <>
                                    <br />
                                    <KidImage
                                        style={{ marginTop: "1em" }}
                                        src={getImageUrl(fact.person_profile)}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
            {fact.factKind === 1 &&
                (editFact ? (
                    <div>
                        <div id={`${fact.desc_id}-map`} />
                        <Autocomplete
                            style={{ width: "100%" }}
                            value={locationValue}
                            onChange={(
                                evt: any,
                                newValue: {
                                    label: string;
                                    id: number;
                                    lat: number;
                                    lng: number;
                                } | null
                            ) => {
                                if (!newValue) return;
                                setLocationValue(newValue);
                                setNewFact({
                                    ...newFact,
                                    location: newValue.label,
                                    validLocation: true,
                                    locationLatLon: `${newValue.lat}#${newValue.lng}`,
                                });
                            }}
                            inputValue={newFact.location}
                            getOptionLabel={(option) => option.label}
                            options={locationOptions}
                            onInputChange={(
                                evt: any,
                                newValue: string | null,
                                reason: string
                            ) => {
                                if (reason === "reset") return;
                                newValue = newValue || "";
                                setLocationValue(null);
                                setNewFact({
                                    ...newFact,
                                    location: newValue,
                                    locationLatLon: "",
                                    validLocation: false,
                                });

                                if (!newValue) return;

                                const service =
                                    placesService ||
                                    new google.maps.places.PlacesService(
                                        document.createElement("div")
                                    );
                                if (!placesService) setPlacesService(service);
                                service.textSearch(
                                    { query: newValue },
                                    (results: any, status: any) => {
                                        const locations = results.map(
                                            (result: any, index: number) => ({
                                                label:
                                                    result.formatted_address ||
                                                    "",
                                                id: index,
                                                lat: result.geometry.location.lat(),
                                                lng: result.geometry.location.lng(),
                                            })
                                        );
                                        setLocationValue(null);
                                        setLocationOptions(locations);
                                    }
                                );
                            }}
                            filterOptions={(x) => x}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    key={params.id}
                                    label="Location"
                                />
                            )}
                        />
                        <CenterEditRow>
                            <Checkbox
                                checked={newFact.locationDistanceNotif}
                                onChange={() =>
                                    setNewFact({
                                        ...newFact,
                                        locationDistanceNotif:
                                            !newFact.locationDistanceNotif,
                                    })
                                }
                            />
                            Alert me when within &nbsp;
                            <TextField
                                style={{
                                    width: "3em",
                                    lineHeight: "1em",
                                }}
                                inputProps={{ style: { padding: "0" } }}
                                type="number"
                                value={newFact.locationDistanceOption}
                                onChange={(
                                    evt: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                    setNewFact({
                                        ...newFact,
                                        locationDistanceOption: parseInt(
                                            evt.target.value || ""
                                        ),
                                    })
                                }
                            />{" "}
                            &nbsp; miles
                        </CenterEditRow>
                    </div>
                ) : (
                    <div>
                        <b>Location: </b>
                        {fact.location}
                    </div>
                ))}
            {fact.factKind === 2 &&
                (editFact ? (
                    <div>
                        <EditRow>
                            <TextField
                                value={newFact.kidName}
                                label="Kid name (optional)"
                                onChange={(
                                    evt: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                    setNewFact({
                                        ...newFact,
                                        kidName: evt.target.value,
                                    })
                                }
                                placeholder="Kid name (optional)"
                            />
                        </EditRow>
                        <EditRow>
                            <TextField
                                label="Kid age (optional)"
                                placeholder="Kid age (optional)"
                                type="number"
                                value={newFact.kidAge}
                                inputProps={{ min: 0 }}
                                onChange={(
                                    evt: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                    setNewFact({
                                        ...newFact,
                                        kidAge: parseInt(evt.target.value),
                                    })
                                }
                            />
                        </EditRow>
                        <EditRow>
                            <DatePicker
                                label={"Kid birthday (optional)"}
                                defaultValue={
                                    formatDate(newFact.kidBirthdayDate) || ""
                                }
                                onChange={(date: string) =>
                                    setNewFact({
                                        ...newFact,
                                        kidBirthdayDate: date,
                                    })
                                }
                            />
                        </EditRow>
                        <EditRow style={{ position: "relative" }}>
                            <TextField
                                label="Note (optional)"
                                value={newFact.description}
                                multiline
                                onChange={(
                                    evt: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                    setNewFact({
                                        ...newFact,
                                        description: evt.target.value,
                                    })
                                }
                                style={{ width: "100%" }}
                                inputProps={{ maxLength: 140 }}
                            />
                            <FactCreationDate
                                style={{
                                    position: "absolute",
                                    bottom: "0rem",
                                    right: "1rem",
                                }}
                            >
                                (
                                {DESCRIPTION_MAX_LENGTH -
                                    newFact.description.length}{" "}
                                characters left)
                            </FactCreationDate>
                        </EditRow>
                        <EditRow>
                            <Button
                                variant={
                                    newFact.kidOptionSelected === 0
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() =>
                                    setNewFact({
                                        ...newFact,
                                        kidOptionSelected: 0,
                                    })
                                }
                            >
                                Son
                            </Button>
                            <Button
                                variant={
                                    newFact.kidOptionSelected === 1
                                        ? "contained"
                                        : "outlined"
                                }
                                style={{ margin: "0 1em" }}
                                onClick={() =>
                                    setNewFact({
                                        ...newFact,
                                        kidOptionSelected: 1,
                                    })
                                }
                            >
                                Daughter
                            </Button>
                            <Button
                                variant={
                                    newFact.kidOptionSelected === 2
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() =>
                                    setNewFact({
                                        ...newFact,
                                        kidOptionSelected: 2,
                                    })
                                }
                            >
                                Unknown
                            </Button>
                        </EditRow>
                        <EditRow>
                            <img
                                style={{
                                    maxWidth: "50%",
                                    maxHeight: "10em",
                                    marginTop: "1em",
                                }}
                                src={
                                    personProfile
                                        ? newFact.person_profile
                                        : getImageUrl(fact.person_profile)
                                }
                                alt=""
                            />
                            <Button
                                tabIndex={0}
                                onClick={() => {
                                    preventBlurTimeout.current =
                                        new Date().getTime() +
                                        PREVENT_BLUR_TIMEOUT;
                                }}
                                component="label"
                            >
                                Attach picture
                                <input
                                    ref={inputRef}
                                    id={`${fact.desc_id}-kid-picture-input`}
                                    style={{ display: "none" }}
                                    type="file"
                                    accept="image/*"
                                    onChange={(
                                        evt: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        if (!evt.target.files) return;
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setNewFact((newFact) => ({
                                                ...newFact,
                                                person_profile:
                                                    (reader.result &&
                                                        reader.result.toString()) ||
                                                    "",
                                            }));
                                        };
                                        reader.readAsDataURL(
                                            evt.target.files[0]
                                        );
                                        setPersonProfile(evt.target.files[0]);
                                    }}
                                />
                            </Button>
                        </EditRow>
                    </div>
                ) : (
                    <div>
                        <b>
                            {(fact.kidOptionSelected === 0 && "Son") ||
                                (fact.kidOptionSelected === 1 && "Daughter") ||
                                "Child"}
                            {((fact.kidName || fact.kidAge) && ":") || ""}{" "}
                            &nbsp;
                        </b>
                        {fact.kidName}{" "}
                        {(fact.kidAge && `(Age ${fact.kidAge})`) || ""} <br />
                        {fact.description}
                        {fact.person_profile && (
                            <>
                                <br />
                                <KidImage
                                    style={{ marginTop: "1em" }}
                                    src={getImageUrl(fact.person_profile)}
                                />
                            </>
                        )}
                    </div>
                ))}

            {fact.factKind === 3 &&
                (editFact ? (
                    <div>
                        <EditRow>
                            <DatePicker
                                defaultValue={formatDate(fact.birthdayDate)}
                                onChange={(date: string) => {
                                    setNewFact({
                                        ...newFact,
                                        birthdayDate: date,
                                    });
                                }}
                            />
                        </EditRow>
                    </div>
                ) : (
                    <div>
                        <b>Birthday: </b>
                        <span>{formatDate(fact.birthdayDate)}</span>
                    </div>
                ))}
            {editFact && (
                <CenterEditRow>
                    <Checkbox
                        checked={newFact.factNotif || false}
                        onChange={() =>
                            setNewFact({
                                ...newFact,
                                factNotif: !newFact.factNotif,
                            })
                        }
                    />{" "}
                    <span style={{ marginRight: "0.3rem" }}>Remind me </span>
                    <Dropdown
                        paperClasses={`${fact.desc_id}`}
                        options={
                            <>
                                <MenuItem
                                    className={fact.desc_id}
                                    onClick={() =>
                                        handleChangeFactReminder("12#Months")
                                    }
                                >
                                    Yearly
                                </MenuItem>
                                <MenuItem
                                    className={fact.desc_id}
                                    onClick={() =>
                                        handleChangeFactReminder("1#Weeks")
                                    }
                                >
                                    Weekly
                                </MenuItem>
                                <MenuItem
                                    className={fact.desc_id}
                                    onClick={() =>
                                        handleChangeFactReminder("1#Months")
                                    }
                                >
                                    Monthly
                                </MenuItem>
                                <MenuItem
                                    className={fact.desc_id}
                                    onClick={() =>
                                        handleChangeFactReminder("6#Months")
                                    }
                                >
                                    Every 6 Months
                                </MenuItem>
                                <MenuItem
                                    className={fact.desc_id}
                                    onClick={() => {
                                        setIsSettingSpecificReminderDate(true);
                                    }}
                                >
                                    Specific date
                                </MenuItem>
                            </>
                        }
                    >
                        <PrimaryText
                            tabIndex={0}
                            className={fact.desc_id}
                            style={{
                                textDecoration: "underline",
                                cursor: "pointer",
                            }}
                        >
                            {getReminderText(newFact.remindOnDate || "")}
                        </PrimaryText>
                    </Dropdown>
                    {isSettingSpecificReminderDate && (
                        <div style={{ display: "none" }}>
                            <DatePicker
                                autoFocus
                                defaultValue={
                                    !newFact.remindOnDate ||
                                    (newFact.remindOnDate &&
                                        newFact.remindOnDate.includes("#"))
                                        ? dayjs().toISOString()
                                        : dayjs
                                              .unix(
                                                  parseInt(
                                                      newFact.remindOnDate ||
                                                          "0"
                                                  )
                                              )
                                              .toISOString()
                                }
                                onClose={() =>
                                    setIsSettingSpecificReminderDate(false)
                                }
                                onAccept={(date: Dayjs | null) => {
                                    if (!date) return;
                                    // TODO: divide by 1000 if it works with seconds
                                    setNewFact({
                                        ...newFact,
                                        remindOnDate: (
                                            date.valueOf() / 1000
                                        ).toString(),
                                    });
                                }}
                            />
                        </div>
                    )}
                </CenterEditRow>
            )}

            {fact.factKind === 3 && editFact && (
                <CenterEditRow>
                    <Checkbox
                        checked={newFact.inAdvanceFactNotif}
                        onChange={() =>
                            setNewFact({
                                ...newFact,
                                inAdvanceFactNotif: !newFact.inAdvanceFactNotif,
                            })
                        }
                    />
                    <span style={{ marginRight: "0.3rem" }}>Start</span>
                    <Dropdown
                        paperClasses={`${fact.desc_id}`}
                        options={
                            <>
                                <MenuItem
                                    onClick={() =>
                                        setNewFact({
                                            ...newFact,
                                            inAdvanceFirstWord: "daily",
                                        })
                                    }
                                >
                                    daily
                                </MenuItem>
                                <MenuItem
                                    onClick={() =>
                                        setNewFact({
                                            ...newFact,
                                            inAdvanceFirstWord: "weekly",
                                        })
                                    }
                                >
                                    weekly
                                </MenuItem>
                                <MenuItem
                                    onClick={() =>
                                        setNewFact({
                                            ...newFact,
                                            inAdvanceFirstWord: "monthly",
                                        })
                                    }
                                >
                                    monthly
                                </MenuItem>
                            </>
                        }
                    >
                        <PrimaryText
                            tabIndex={0}
                            className={fact.desc_id}
                            style={{
                                cursor: "pointer",
                                textDecoration: "underline",
                            }}
                        >
                            {newFact.inAdvanceFirstWord}
                        </PrimaryText>{" "}
                    </Dropdown>
                    <span style={{ margin: "0 0.3rem" }}>nudges</span>
                    <TextField
                        style={{
                            width: "3em",
                            lineHeight: "1em",
                        }}
                        inputProps={{
                            style: {
                                padding: "0",
                                textAlign: "center",
                            },
                        }}
                        type="number"
                        value={parseInt(newFact.inAdvanceSecondWord || "1")}
                        onChange={(
                            evt: React.ChangeEvent<HTMLInputElement>
                        ) => {
                            setNewFact({
                                ...newFact,
                                inAdvanceSecondWord: evt.target.value,
                            });
                        }}
                    />{" "}
                    &nbsp;
                    <Dropdown
                        paperClasses={`${fact.desc_id}`}
                        options={
                            <>
                                <MenuItem
                                    onClick={() =>
                                        setNewFact({
                                            ...newFact,
                                            inAdvanceThirdWord: "days",
                                        })
                                    }
                                >
                                    days
                                </MenuItem>
                                <MenuItem
                                    onClick={() =>
                                        setNewFact({
                                            ...newFact,
                                            inAdvanceThirdWord: "weeks",
                                        })
                                    }
                                >
                                    weeks
                                </MenuItem>
                                <MenuItem
                                    onClick={() =>
                                        setNewFact({
                                            ...newFact,
                                            inAdvanceThirdWord: "months",
                                        })
                                    }
                                >
                                    months
                                </MenuItem>
                            </>
                        }
                    >
                        <PrimaryText
                            tabIndex={0}
                            className={fact.desc_id}
                            style={{
                                cursor: "pointer",
                                textDecoration: "underline",
                            }}
                        >
                            {newFact.inAdvanceThirdWord}
                        </PrimaryText>
                    </Dropdown>{" "}
                    <span style={{ marginLeft: "0.3rem" }}>in advance</span>
                </CenterEditRow>
            )}
            {!editFact ? (
                <div />
            ) : (
                <CenterEditRow
                    style={{
                        position: "absolute",
                        bottom: "0",
                        right: "0",
                        justifyContent: "space-between",
                        width: "100%",
                    }}
                >
                    <div>
                        <Button color="error" onClick={handleDelete}>
                            Delete
                        </Button>
                    </div>
                    <div>
                        <Button color="grey2" onClick={handleDiscard}>
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleSave}>
                            Save
                        </Button>
                    </div>
                </CenterEditRow>
            )}
            {!editFact && (
                <div
                    style={{ position: "absolute", bottom: "0", right: "1em" }}
                >
                    <FactCreationDate>
                        {dayjs(fact.created_date).format("MMM D, YYYY")}
                    </FactCreationDate>
                </div>
            )}
            {!editFact && !props.hideReminder && (
                <div
                    style={{
                        position: "absolute",
                        top: "0.5rem",
                        right: "2.2rem",
                    }}
                >
                    {fact.sharedFact && (
                        <LockIcon style={{ marginRight: "1rem" }} />
                    )}
                    {fact.factNotif ? (
                        <BellIcon onClick={handleToggleNotifications} />
                    ) : (
                        <BellSlashIcon onClick={handleToggleNotifications} />
                    )}
                </div>
            )}
            {sharedFactWarning && (
                <Confirm
                    open={sharedFactWarning}
                    text={`This fact was shared with you and can only be edited by its owner`}
                    confirmText="Ok"
                    cancelText=" "
                    onCancel={() => {}}
                    onConfirm={(evt: React.SyntheticEvent) => {
                        evt.stopPropagation();
                        evt.preventDefault();
                        setSharedFactWarning(false);
                    }}
                />
            )}
        </Container>
    );
};

export default connect(mapStateToProps, {})(FactCard);
